import { useMutation } from '@tanstack/react-query';
import { type ReactElement, useState } from 'react';

import { Button } from '@autop/ui/src/button';

import { useSession } from '@/entities/session';
import { setAccessToken, useTRPC } from '@/shared/api';

interface WithdrawSectionProps {
  /** 탈퇴가 끝났음을 페이지에 알린다 — 세션이 비므로 페이지가 화면을 바꿔야 한다. */
  onWithdrawn: () => void;
}

/**
 * 회원 탈퇴.
 *
 * **평소에는 버튼 하나로 접어 둔다.** 계정 화면은 소셜 연결을 보러 들르는 곳이기도 한데,
 * 경고문과 비밀번호 칸이 늘 펼쳐져 있으면 그 화면 전체가 위험한 화면처럼 보인다.
 * 탈퇴는 일생에 한 번 하는 일이므로 한 번의 클릭을 더 받는 편이 낫다.
 *
 * **비밀번호를 묻지 않는다** (오너 결정, 2026-09-10). 한때 물었으나 **화면이 그 물음을
 * 던질 자격이 없었다** — `auth.me` 는 회원이 비밀번호로 가입했는지 소셜로 가입했는지
 * 알려주지 않아 "비밀번호로 가입하셨다면 입력하세요"라고 되물어야 했고, 1년 전 가입
 * 수단을 기억하지 못하는 사람은 빈칸으로 보냈다가 「비밀번호가 올바르지 않습니다」를
 * 맞고 탈퇴하지 못했다.
 *
 * 남은 방어는 **확인 체크박스 하나**다. 확인창만 두면 사람은 읽지 않고 「예」를 누르므로,
 * 경고문을 눈으로 지나가게 만드는 장치가 필요하다.
 */
export function WithdrawSection({ onWithdrawn }: WithdrawSectionProps): ReactElement {
  const trpc = useTRPC();
  const clearSession = useSession((state) => state.signOut);

  const [isOpen, setOpen] = useState(false);
  const [isConfirmed, setConfirmed] = useState(false);

  /**
   * 접으면서 입력을 비운다. 그만둔 뒤 다시 열었을 때 체크가 남아 있으면 **경고를 읽지
   * 않은 채로 버튼이 이미 열려 있는** 상태가 된다 — 이 화면이 막으려던 바로 그 상황이다.
   */
  const close = () => {
    setOpen(false);
    setConfirmed(false);
    withdraw.reset();
  };

  const withdraw = useMutation(
    trpc.auth.withdraw.mutationOptions({
      onSuccess: () => {
        // 서버가 세션과 쿠키를 이미 지웠다. 화면에 남은 토큰까지 버려야 다음 요청이
        //   죽은 토큰을 들고 나가지 않는다.
        setAccessToken(null);
        clearSession();
        onWithdrawn();
      },
    }),
  );

  return (
    <section aria-labelledby="account-withdraw-heading" className="flex flex-col gap-3">
      <h2
        id="account-withdraw-heading"
        className="text-[1.0625rem] font-semibold tracking-[-0.03em]"
      >
        회원 탈퇴
      </h2>

      {isOpen ? null : (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-5">
          <p className="flex-1 text-[13px] leading-[1.8] text-muted-foreground">
            계정과 이용 기록을 지웁니다. 되돌릴 수 없습니다.
          </p>
          <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>
            회원 탈퇴
          </Button>
        </div>
      )}

      {!isOpen ? null : (
        <form
          className="flex flex-col gap-4 rounded-lg border border-destructive border-l-[3px] p-5"
          onSubmit={(event) => {
            event.preventDefault();
            withdraw.mutate({});
          }}
        >
          <p className="text-[13px] leading-[1.8] text-muted-foreground">
            탈퇴하면 보유한 클래스와 자료에 접근할 수 없게 되며 복구되지 않습니다. 같은 이메일로
            다시 가입하실 수는 있지만, 이전에 받으신 권한은 돌아오지 않습니다.
          </p>

          <label htmlFor="account-withdraw-confirm" className="flex items-start gap-2 text-[14px]">
            <input
              id="account-withdraw-confirm"
              type="checkbox"
              checked={isConfirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
              className="mt-0.5 size-4 shrink-0 accent-primary"
            />
            <span>위 내용을 확인했습니다</span>
          </label>

          {withdraw.error === null ? null : (
            <p role="alert" className="text-[13px] text-destructive">
              {withdraw.error.message}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              variant="danger"
              size="sm"
              disabled={!isConfirmed || withdraw.isPending}
            >
              탈퇴하기
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={withdraw.isPending}
              onClick={close}
            >
              그만두기
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
